import { useEffect, useId, useState } from "react";
import { Input } from "./input";
import { Label } from "./label";

export interface RangeSliderProps {
  label: string;
  /** Lowest/highest selectable values. */
  bound: { min: number; max: number };
  /** Current filter values as strings ("" = unset → handle sits at the bound). */
  valueMin: string;
  valueMax: string;
  /** Emits the new (min, max) as strings; "" means "no bound" (handle at extreme). */
  onChange: (min: string, max: string) => void;
  /** Formats the live value labels (e.g. as currency). */
  format?: (n: number) => string;
  /** The number boxes are edited in these units (e.g. 1_000_000 → type "2.5" for $2.5M). */
  unit?: number;
  /** Suffix shown inside the boxes for `unit` (e.g. "M"). */
  unitLabel?: string;
  /** Prefix shown inside the boxes (e.g. "$"). */
  prefix?: string;
  /** Track curve exponent: higher spends more of the track on the low end.
   *  Defaults to CURVE (dollar ranges); smaller ranges want a gentler curve. */
  curve?: number;
}

// Slider positions run 0..STEPS on a cubic curve so the low end, where most
// listings sit, gets real resolution: on a $0–$500M span a quarter of the
// track covers up to ~$8M instead of $125M.
const STEPS = 1000;
const CURVE = 3;

/** Rounds to 2 significant figures so dragged values read cleanly ($4.1M, not $4,096,000). */
function niceRound(v: number): number {
  if (v <= 0) return 0;
  const mag = Math.pow(10, Math.floor(Math.log10(v)) - 1);
  return Math.round(v / mag) * mag;
}

/** Drops float noise and trailing zeros: 2.5000000001 → "2.5", 500 → "500". */
function trimNumber(n: number): string {
  return String(Number(n.toFixed(4)));
}

/**
 * Dual-handle range slider with two compact, synced number inputs.
 * A value equal to its bound extreme is treated as "unset" and emitted as ""
 * so the filter doesn't needlessly exclude listings (incl. those missing the metric).
 */
export function RangeSlider({
  label, bound, valueMin, valueMax, onChange, format, unit = 1, unitLabel, prefix, curve = CURVE,
}: RangeSliderProps) {
  const id = useId();
  const span = bound.max - bound.min;
  const disabled = span <= 0;

  // Resolve the active numeric handle positions, defaulting to the bounds.
  const lo = valueMin !== "" ? Number(valueMin) : bound.min;
  const hi = valueMax !== "" ? Number(valueMax) : bound.max;

  const toPos = (v: number) =>
    span > 0 ? Math.round(STEPS * Math.pow(Math.min(Math.max((v - bound.min) / span, 0), 1), 1 / curve)) : 0;
  const fromPos = (p: number) => {
    if (p <= 0) return bound.min;
    if (p >= STEPS) return bound.max;
    return bound.min + niceRound(span * Math.pow(p / STEPS, curve));
  };

  const emit = (nextLo: number, nextHi: number) => {
    onChange(
      nextLo <= bound.min ? "" : String(nextLo),
      nextHi >= bound.max ? "" : String(nextHi),
    );
  };

  const loPos = toPos(lo);
  const hiPos = toPos(hi);

  return (
    <div className="flex-1 min-w-[180px] space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</Label>
        <span className="text-[0.7rem] font-semibold text-foreground tabular-nums">
          {format ? format(lo) : lo} – {format ? format(hi) : hi}
        </span>
      </div>

      <div className="range-slider">
        {/* Track + selected segment */}
        <div className="pointer-events-none absolute top-1/2 left-0 h-1.5 w-full -translate-y-1/2 rounded-full bg-border" />
        <div
          className="pointer-events-none absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-primary"
          style={{ left: `${(loPos / STEPS) * 100}%`, width: `${Math.max(0, ((hiPos - loPos) / STEPS) * 100)}%` }}
        />
        <input
          type="range"
          aria-label={`${label} minimum`}
          min={0}
          max={STEPS}
          step={1}
          value={loPos}
          disabled={disabled}
          // When both handles meet near the top, the min handle must sit above
          // the max one or it can never be dragged back down.
          style={{ zIndex: loPos > STEPS / 2 ? 2 : 1 }}
          onChange={(e) => emit(Math.min(fromPos(Number(e.target.value)), hi), hi)}
        />
        <input
          type="range"
          aria-label={`${label} maximum`}
          min={0}
          max={STEPS}
          step={1}
          value={hiPos}
          disabled={disabled}
          onChange={(e) => emit(lo, Math.max(fromPos(Number(e.target.value)), lo))}
        />
      </div>

      {/* Synced editable boxes for precise entry, in `unit`s */}
      <div className="flex items-center gap-2">
        <BoundInput
          id={`${id}-min`}
          ariaLabel={`${label} minimum`}
          value={lo}
          unit={unit}
          prefix={prefix}
          unitLabel={unitLabel}
          disabled={disabled}
          fallback={bound.min}
          // Valid only within the bounds and not above the current max.
          isValid={(n) => n >= bound.min && n <= hi}
          clamp={(n) => Math.min(Math.max(n, bound.min), hi)}
          onCommit={(n) => emit(n, hi)}
        />
        <span className="text-xs text-muted-foreground">–</span>
        <BoundInput
          id={`${id}-max`}
          ariaLabel={`${label} maximum`}
          value={hi}
          unit={unit}
          prefix={prefix}
          unitLabel={unitLabel}
          disabled={disabled}
          fallback={bound.max}
          isValid={(n) => n <= bound.max && n >= lo}
          clamp={(n) => Math.max(Math.min(n, bound.max), lo)}
          onCommit={(n) => emit(lo, n)}
        />
      </div>
    </div>
  );
}

interface BoundInputProps {
  id: string;
  ariaLabel: string;
  /** Current value in base units (dollars). */
  value: number;
  unit: number;
  prefix?: string;
  unitLabel?: string;
  disabled: boolean;
  /** Value an emptied box falls back to on blur (its bound → "unset"). */
  fallback: number;
  isValid: (n: number) => boolean;
  clamp: (n: number) => number;
  onCommit: (n: number) => void;
}

/**
 * Number box that keeps its own draft while typing. Clamping on every keystroke
 * made it impossible to type e.g. a max of 5 when the min was 2 (the first
 * digit snapped straight back). The draft is committed live once it forms a
 * valid range, and clamped only on blur / Enter.
 */
function BoundInput({
  id, ariaLabel, value, unit, prefix, unitLabel, disabled, fallback, isValid, clamp, onCommit,
}: BoundInputProps) {
  const display = trimNumber(value / unit);
  const [draft, setDraft] = useState(display);
  const [focused, setFocused] = useState(false);

  // Follow external changes (slider drags, chip clears) while not being edited.
  useEffect(() => {
    if (!focused) setDraft(display);
  }, [display, focused]);

  // Two decimals, not whole units: a multiple box (unit 1) must keep "3.5";
  // for the $ boxes it's cents, which changes nothing.
  const toBase = (raw: string) => Math.round(Number(raw) * unit * 100) / 100;

  const finalize = () => {
    const n = draft.trim() === "" ? fallback : toBase(draft);
    const next = Number.isFinite(n) ? clamp(n) : value;
    setDraft(trimNumber(next / unit));
    if (next !== value) onCommit(next);
  };

  return (
    <div className="relative flex-1">
      {prefix && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {prefix}
        </span>
      )}
      <Input
        id={id}
        aria-label={ariaLabel}
        type="number"
        inputMode="decimal"
        step="any"
        value={draft}
        disabled={disabled}
        onFocus={(e) => { setFocused(true); e.target.select(); }}
        onBlur={() => { setFocused(false); finalize(); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); finalize(); }
        }}
        onChange={(e) => {
          const raw = e.target.value;
          setDraft(raw);
          if (raw.trim() === "") return;
          const n = toBase(raw);
          if (Number.isFinite(n) && isValid(n)) onCommit(n);
        }}
        className={`h-8 text-xs tabular-nums ${prefix ? "pl-6" : ""} ${unitLabel ? "pr-7" : ""}`}
      />
      {unitLabel && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {unitLabel}
        </span>
      )}
    </div>
  );
}
